<template>
    <DropdownMenu>
        <DropdownMenuTrigger as-child>
            <!-- 모바일: 옆 아이콘 버튼(h-9 w-9)들과 높이·정렬 통일 (컴팩트 패딩, 언어명 숨김) -->
            <Button
                variant="ghost"
                class="flex items-center font-medium justify-center h-9 px-1.5 md:h-10 md:px-4 md:py-2 text-sm"
            >
                <!-- 국기: 번들된 SVG (이모지는 Windows 에서 'KR' 글자로 떨어진다) -->
                <CountryFlag
                    :code="languageCountryCode(currentLocale)"
                    :alt="currentLangName"
                    size-class="w-6 h-[18px] md:mr-2"
                />
                <span class="hidden md:flex">{{ currentLangName }}</span>
                <ChevronDown class="ml-0.5 md:ml-1 h-4 w-4 flex-shrink-0" />
            </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" class="w-48">
            <DropdownMenuItem
                v-for="lang in configStore.availableLanguages"
                :key="lang.code"
                @click="setLanguage(lang.code)"
                :class="{ 'bg-accent': lang.code === currentLocale }"
            >
                <CountryFlag
                    :code="languageCountryCode(lang.code)"
                    :alt="lang.langName"
                    size-class="w-6 h-[18px] mr-2"
                />
                <!-- 언어 이름: DB codeName(자국어) 직접 표시 — i18n 키 의존 제거 -->
                {{ lang.langName }}
            </DropdownMenuItem>
        </DropdownMenuContent>
    </DropdownMenu>
</template>

<script setup>
import { computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useConfigStore } from '@stores/config';
import { ChevronDown } from 'lucide-vue-next';
import { Button } from '@/components/ui/button';
import CountryFlag from '@/components/common/CountryFlag.vue';
import { languageCountryCode } from '@/utils/countryFlags';
import {
    DropdownMenu,
    DropdownMenuTrigger,
    DropdownMenuContent,
    DropdownMenuItem,
} from '@/components/ui/dropdown-menu';

const { locale } = useI18n();
const configStore = useConfigStore();

// Current locale
const currentLocale = computed(() => locale.value);

// 현재 locale 의 활성 언어 정보 (availableLanguages 기준 — DB 비활성 우회 방지).
// DB 머지된 langList 도 fallback 으로 활용.
const currentLangInfo = computed(() => {
    const fromActive = configStore.availableLanguages.find(l => l.code === locale.value);
    if (fromActive) return fromActive;
    const fromList = configStore.langList[locale.value] || configStore.langList[configStore.fallbackLang];
    return fromList ? { code: locale.value, ...fromList } : null;
});

// 트리거 표시용 언어명 (DB codeName / 자국어)
const currentLangName = computed(() => currentLangInfo.value?.langName || 'English');


// Set language
function setLanguage(langCode) {
    locale.value = langCode;
    configStore.setLanguage(langCode);
}

onMounted(async () => {
    // DB(SUPPORTED_LANGUAGES) 시드 기반 동적 언어 목록 로드.
    // 실패해도 fallback langList 로 동작하므로 await 가 화면 차단하지 않음.
    await configStore.loadSupportedLanguages();

    // store action 으로 저장된 locale 읽기 (legacy 'site-locale' 키도 1회 마이그레이션).
    const savedLocale = configStore.readSavedLocale();
    const available = configStore.availableLanguages;

    // 활성 목록이 비어있으면 화면 깨지지 않도록 현재 locale 유지.
    if (available.length === 0) return;

    // 우선순위: 저장값(활성에 있으면) -> 현재 locale(활성에 있으면 유지)
    //         -> fallbackLang -> 첫 번째 활성 언어
    // 초기 locale 이 비활성 언어인 경우(DB 에서 ko 비활성화 등)에도 강제 전환.
    const nextLocale =
        available.find(l => l.code === savedLocale)?.code
        || available.find(l => l.code === locale.value)?.code
        || available.find(l => l.code === configStore.fallbackLang)?.code
        || available[0].code;

    if (nextLocale !== locale.value) {
        locale.value = nextLocale;
    }
    configStore.setLanguage(nextLocale);
});
</script>
